import _ from 'lodash';
import { CategoryLevel } from '../../../../../../reducers/gerenciadorPrecos/segmentos';
import { CacheDataItem } from '../../../../../../reducers/ipa/limits/filters/cattegory';
import { StoreTypeWithoutAllStores } from '../../../../../../reducers/ipa/limits/filters/store';
import { RegisterLimitStateProps } from '../../../../../../reducers/ipa/limits/root';
import { getValidData } from '../../../../RevisaoPrecos/utils/getValidData';
import { SegmentsCurve } from '../../RegrasDePreco/lib/utils';
import { GetLimitDataRawResponse } from './mock';
import { RegisterLimitSchemaProps } from './schema';
import { getSegmentsModel } from './utils';

export const segmentsCurve = ['infoPriceSensibility', 'infoPriceAbcClass', 'infoPriceGlobalAbcClass', 'sensibilityType', 'abcClass'] as const;

type RegisterLimitModel = { formData: RegisterLimitSchemaProps; filters: RegisterLimitStateProps };

export const getRegisterLimitModel = ({ formData, filters }: RegisterLimitModel, limitId?: string) => {
  const mapClusterItem = (cluster: string) => ({ id: cluster });
  const { marginType, ...formValues } = formData;
  const isObjectiveMargin = marginType === 'objectiveMargin';

  const parseFiltersData = ({ cattegory, store, segment }: RegisterLimitStateProps) => {
    return {
      ...segment.data.value,
      segments: getSegmentsModel({ value: cattegory.value, cache: cattegory.cache }),
      storeIds: store.data.value['individual-store'],
      clusters: store.data.value.clusters.map(mapClusterItem),
    };
  };

  const validData = getValidData({
    limitId,
    ...formValues,
    ...parseFiltersData(filters),
    clientId: JSON.parse(localStorage.getItem('cliente') as string)?.codigo,
    author: JSON.parse(localStorage.getItem('usuario') as string)?.email,
  });

  return {
    ...validData,
    minMargin: isObjectiveMargin ? null : formValues.minMargin,
    maxMargin: isObjectiveMargin ? null : formValues.maxMargin,
    downwardVariation: isObjectiveMargin ? formValues.downwardVariation : null,
    upwardVariation: isObjectiveMargin ? formValues.upwardVariation : null,
    minCpi: formValues.minCpi,
    maxCpi: formValues.maxCpi,
  };
};

export type RegisterLimitModelReturn = ReturnType<typeof getRegisterLimitModel>;

type SingleFilterData<T = string> = Partial<{ key: T; value: string[]; cache?: DataItem[] }>;

export const parseGetLimitDataResponse = (data: GetLimitDataRawResponse) => {
  const formatPercentInput = (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    return Number((value * 100).toFixed(1)).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  const formatVariationInput = (value: number | null | undefined) => {
    if (value === null || value === undefined) return null;
    return Number(value.toFixed(1)).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  const hasObjectiveVariation = data.downwardVariation != null || data.upwardVariation != null;
  const marginType: RegisterLimitSchemaProps['marginType'] = hasObjectiveVariation ? 'objectiveMargin' : 'margin';

  const parseFormData = ({
    name,
    minCpi,
    minMargin,
    maxCpi,
    maxMargin,
    downwardVariation,
    upwardVariation,
    adjustPricesAccordingToLimits,
  }: GetLimitDataRawResponse) => {
    return {
      name,
      marginType,
      adjustPricesAccordingToLimits: adjustPricesAccordingToLimits ?? false,
      minMargin: formatPercentInput(minMargin),
      maxMargin: formatPercentInput(maxMargin),
      downwardVariation: formatVariationInput(downwardVariation),
      upwardVariation: formatVariationInput(upwardVariation),
      minCpi: formatPercentInput(minCpi),
      maxCpi: formatPercentInput(maxCpi),
    };
  };

  const parseCattegory = ({ segments }: GetLimitDataRawResponse) => {
    const cattegories = new Map<CategoryLevel, { value: string[]; cache: CacheDataItem[] }>();

    segments.forEach(({ level, value, description }) => {
      if (!cattegories.has(level)) cattegories.set(level, { value: [], cache: [] });
      const categoryData = cattegories.get(level);
      if (!categoryData) return;
      categoryData.value.push(value);
      categoryData.cache.push(level.match(/productIds/) ? { label: `${value} - ${description}`, value, description } : { label: value, value, description });
    });

    const data = Object.fromEntries(cattegories.entries());

    return { rows: Object.keys(data).length, data: data };
  };

  const parseStore = ({ storeIds, clusters }: GetLimitDataRawResponse) => {
    const store: SingleFilterData<StoreTypeWithoutAllStores> = {};

    if (clusters?.length) {
      store.key = 'clusters';
      store.value = clusters.map(cluster => cluster.id);
      store.cache = clusters.map(cluster => ({ label: cluster.label, value: cluster.id }));
    }

    if (storeIds?.length) {
      store.key = 'individual-store';
      store.value = storeIds;
    }

    return store;
  };

  const parseSegments = (filters: GetLimitDataRawResponse) => {
    const segments = _.pick(filters, segmentsCurve);

    const segment: SingleFilterData<SegmentsCurve> = {};

    Object.entries(segments).forEach(([key, value]) => {
      if (value?.length && segmentsCurve.includes(key as SegmentsCurve)) {
        segment.key = key as SegmentsCurve;
        segment.value = value;
      }
    });

    return segment;
  };

  return { form: parseFormData(data), filters: { cattegory: parseCattegory(data), store: parseStore(data), segments: parseSegments(data) } };
};

export type GetLimitDataParsedResponse = ReturnType<typeof parseGetLimitDataResponse>;
